/**
 * SkillQuest Prefer-over component registry (Train B / P6).
 * Agents and assert scripts treat `status: "preferred"` as mandatory for new UI.
 * Train C may update importPaths for relocated DS primitives; keep registry in sync.
 */

export interface DesignSystemComponentEntry {
	id: string;
	title: string;
	importPath: string;
	exportName: string;
	category:
		| "action"
		| "chrome"
		| "overlay"
		| "feedback"
		| "data-display"
		| "brand"
		| "surface"
		| "form"
		| "quote"
		| "disclosure"
		| "loader"
		| "atmosphere"
		| "collection"
		| "dnd";
	status: "preferred" | "allowed" | "legacy" | "avoid";
	supersedes?: string[];
	themeModes?: Array<"light" | "dark" | "system">;
	supportsLocalTone?: boolean;
	supportsContrastVariant?: boolean;
	sqColorModePolicy?: "static" | "theme-pair" | "css-dark-variant";
	a11yNotes?: string;
	i18nNotes?: string;
	notes?: string;
	relatedLearningsAnchor?: string;
}

export const DESIGN_SYSTEM_COMPONENTS: DesignSystemComponentEntry[] = [
	// —— Actions ——
	{
		id: "primary-cta",
		title: "PrimaryCta",
		importPath: "@repo/ui/components/design-system/cta/primary-cta",
		exportName: "PrimaryCta",
		category: "action",
		status: "preferred",
		supersedes: ["shadcn Button as product CTA", "GlowButton in Operate"],
		themeModes: ["light", "dark", "system"],
		sqColorModePolicy: "static",
		notes: "Presentational SoT in design-system. Marketing analytics: MarketingPrimaryCta via @shared/lib/cta/marketing-cta only.",
	},
	{
		id: "secondary-cta",
		title: "SecondaryCta",
		importPath: "@repo/ui/components/design-system/cta/secondary-cta",
		exportName: "SecondaryCta",
		category: "action",
		status: "preferred",
		supersedes: ["shadcn Button variant outline as product CTA"],
	},
	{
		id: "destructive-cta",
		title: "DestructiveCta",
		importPath: "@repo/ui/components/design-system/cta/destructive-cta",
		exportName: "DestructiveCta",
		category: "action",
		status: "preferred",
		supersedes: ["shadcn Button variant destructive as product CTA"],
	},
	{
		id: "glow-button",
		title: "GlowButton",
		importPath: "@repo/ui/components/tailwind-plus/glow-button",
		exportName: "GlowButton",
		category: "action",
		status: "allowed",
		supersedes: [],
		notes: "Marketing / Persuade glow surfaces only — never substitute for PrimaryCta in Operate.",
	},
	{
		id: "circle-button",
		title: "CircleButton",
		importPath: "@repo/ui/components/custom/circle-button",
		exportName: "CircleButton",
		category: "action",
		status: "preferred",
		supersedes: ["Icon-only shadcn Button for HUD circles"],
		themeModes: ["light", "dark", "system"],
		supportsLocalTone: true,
		sqColorModePolicy: "css-dark-variant",
		notes: 'Prefer tone="system" + dark: utilities.',
	},

	// —— Chrome / tabs ——
	{
		id: "glass-mode-tab-chrome",
		title: "GlassModeTabChrome",
		importPath: "@repo/ui/components/custom/glass-mode-tab-chrome",
		exportName: "GlassModeTabChrome",
		category: "chrome",
		status: "preferred",
		supersedes: ["shadcn Tabs for glass mode strips"],
		supportsLocalTone: true,
	},
	{
		id: "shop-mode-tabs",
		title: "ShopModeTabs",
		importPath: "@saas/shop/ShopModeTabs",
		exportName: "ShopModeTabs",
		category: "chrome",
		status: "preferred",
		supersedes: ["Ad-hoc shop tab strips"],
	},
	{
		id: "entity-page-header-tab-strips",
		title: "buildEntityPageHeaderTabStrips",
		importPath:
			"@saas/shared/components/entity-page-identity/EntityPageHeaderTabStrips",
		exportName: "buildEntityPageHeaderTabStrips",
		category: "chrome",
		status: "preferred",
		supersedes: ["One-off entity header tab builders"],
	},

	// —— Disclosure ——
	{
		id: "accordion-l0",
		title: "AccordionDisclosure / AccordionCollapsePanel",
		importPath: "@repo/ui/components/design-system/accordion",
		exportName: "AccordionDisclosure",
		category: "disclosure",
		status: "preferred",
		supersedes: [
			"@repo/ui Accordion (Radix) for new product chrome",
			"DIY grid-rows expand",
			"PendingActionsAccordion as general accordion",
		],
		a11yNotes:
			"Headless UI Disclosure + AccordionCollapsePanel height animation.",
		notes: "Accordion L0 SoT under design-system/accordion. @shared shims remain. FAQ uses L0.",
	},
	{
		id: "radix-accordion",
		title: "Radix Accordion (shadcn)",
		importPath: "@repo/ui/components/accordion",
		exportName: "Accordion",
		category: "disclosure",
		status: "legacy",
		supersedes: [],
		notes: "Avoid for new product chrome. Legacy call sites allowlisted under docs/design/allowlists/.",
	},

	// —— Forms ——
	{
		id: "field-types",
		title: "FieldTypes (FieldRenderer / FieldShell)",
		importPath: "@repo/ui/components/field-types",
		exportName: "FieldRenderer",
		category: "form",
		status: "preferred",
		supersedes: [
			"Raw Input/Select/Textarea outside FieldTypes",
			"FormMessage for field errors",
		],
		i18nNotes:
			"Labels/errors via useTranslations; contracts in @repo/ui/lib/field-types.",
		relatedLearningsAnchor: "Platform FieldType registry",
		notes: "Mandatory for all product forms including auth.",
	},
	{
		id: "inheritable-field",
		title: "InheritableField",
		importPath: "@repo/ui/components/field-types",
		exportName: "InheritableField",
		category: "form",
		status: "preferred",
		supersedes: [
			"Ad-hoc 'inherited from' hints or reset buttons beside settings fields",
		],
		i18nNotes:
			"originLabel / resetLabel / mixedLabel come translated from the app (create.contentSettings.*).",
		notes:
			"Settings-cascade chrome (create-orchestration-governance R9): wraps any FieldType via render prop; pass slot.labelTrailing to the field. Origin chip, reset-to-inherited when overridden, Mixed for bulk scopes.",
	},
	{
		id: "dialkit",
		title: "DialKit Prefer-over (Slider/Toggle/Select/Color/Pad/Easing/Root)",
		importPath: "@repo/ui/components/design-system/dialkit",
		exportName: "DialKitSlider",
		category: "form",
		status: "preferred",
		supersedes: [
			"Raw from \"dialkit\" in saas/app feature code (C14)",
			"Radix Slider for NumberField showSlider (C18)",
		],
		relatedLearningsAnchor: "O34+O35 FINAL SoT",
		notes:
			"O35.P8 atoms only under packages/ui/**/dialkit*. Dock gated by PRODUCTION.md / isDialKitProductionEnabled; embedded presentations always on.",
	},
	{
		id: "easing-field",
		title: "EasingField",
		importPath: "@repo/ui/components/field-types",
		exportName: "EasingField",
		category: "form",
		status: "preferred",
		supersedes: [
			"Ad-hoc easing string selects outside FieldTypes",
			"Raw DialKit TransitionControl in feature code",
		],
		notes:
			"O35.P8 Prefer-over for EasingV1 (named incl. circularArc + advanced bezier). SeedField + DiscreteScaleField stay SQ until D19/D20.",
	},
	{
		id: "discrete-scale-field",
		title: "DiscreteScaleField",
		importPath: "@repo/ui/components/field-types",
		exportName: "DiscreteScaleField",
		category: "form",
		status: "preferred",
		supersedes: [
			"Ad-hoc Radix Slider for ordinal Effort / discrete scales",
			"SingleSelectField-only Effort chrome",
		],
		notes:
			"Prefer-over discrete ordinal scale (Faster←→Smarter Effort). D19: DialKitSlider Prefer-over for track; Faster/Smarter axis + tick jumps + max beam shell retained. FieldRenderer fieldType discrete_scale.",
	},
	{
		id: "tag-field",
		title: "TagField",
		importPath: "@repo/ui/components/field-types",
		exportName: "TagField",
		category: "form",
		status: "preferred",
		supersedes: [
			"MultiSelectField-only for creatable tags",
			"Ad-hoc chip + combobox kits",
			"Raw shadcn-space combobox-08/10 in app code",
		],
		notes:
			"Prefer-over creatable multi-tag FieldType (combobox-08 multi ∪ combobox-10 creatable). Wire onCreateTag → library.ensureTag. P7.",
	},
	{
		id: "preset-picker",
		title: "PresetPicker",
		importPath: "@repo/ui/components/design-system/preset-picker",
		exportName: "PresetPicker",
		category: "form",
		status: "preferred",
		supersedes: [
			"Custom prompt preset bar (custom-prompt picker controls)",
			"Per-feature preset dropdowns",
		],
		notes:
			"One picker for settings presets and prompt presets (create-orchestration-governance R101/K22): searchable Platform / Organization / Mine groups (Popover + Command, shadcnspace combobox-03 pattern), save-current with owner choice for admins, delete for presets the viewer owns. Platform presets are never manageable.",
	},
	{
		id: "editable-field",
		title: "EditableField",
		importPath: "@repo/ui/components/custom/editable-field",
		exportName: "EditableField",
		category: "form",
		status: "preferred",
		supersedes: ["Second ad-hoc edit-session field kit"],
	},

	// —— Quotes ——
	{
		id: "speech-bubble",
		title: "SpeechBubble",
		importPath: "@repo/ui/components/custom/speech-bubble",
		exportName: "SpeechBubble",
		category: "quote",
		status: "preferred",
		supersedes: ["DIY stacked triangles"],
		themeModes: ["light", "dark", "system"],
		sqColorModePolicy: "css-dark-variant",
		notes: "Portrait speech only — do not mix FlagBanner notch recipes.",
	},
	{
		id: "media-lightbox",
		title: "MediaLightbox",
		importPath:
			"@repo/ui/components/design-system/media-lightbox/MediaLightbox",
		exportName: "MediaLightbox",
		category: "overlay",
		status: "preferred",
		supersedes: [
			"LandingFeaturesLightbox's private lightbox core",
			"ad-hoc image/video zoom overlays",
		],
		themeModes: ["light", "dark", "system"],
		sqColorModePolicy: "static",
		a11yNotes:
			"Focus stays inside while open and returns to the opener on close; arrows, Esc and swipe; no animation under reduced motion.",
		i18nNotes: "All copy via `labels` (`mediaLightbox` namespace).",
		notes: "Topic IMAGE/VIDEO blocks (P14c) and the landing feature screenshots. Video slides can render a live Player via `renderVideo` (D51).",
	},
	{
		id: "block-quote",
		title: "BlockQuote",
		importPath: "@repo/ui/components/design-system/quote/block-quote",
		exportName: "BlockQuote",
		category: "quote",
		status: "preferred",
		supersedes: ["Bare blockquote"],
		notes: "Canonical DS quote path; @repo/ui/components/design-system/quote/block-quote shim remains.",
	},
	{
		id: "flag-banner",
		title: "FlagBanner",
		importPath: "@repo/ui/components/custom/flag-banner",
		exportName: "FlagBanner",
		category: "quote",
		status: "preferred",
		supersedes: ["Speech triangles used as ribbons"],
	},

	// —— Atmosphere / surfaces ——
	{
		id: "border-beam",
		title: "BorderBeam",
		importPath: "@repo/ui/components/design-system/border-beam",
		exportName: "BorderBeam",
		category: "atmosphere",
		status: "preferred",
		supersedes: ["One-off orbit beams"],
		a11yNotes: "Honor prefers-reduced-motion (static rim).",
		relatedLearningsAnchor: "Composer toolbar redesign",
		notes: "Canonical DS path; @shared shim remains.",
	},
	{
		id: "sq-sprite-sheet",
		title: "SqSpriteSheet",
		importPath: "@repo/ui/components/design-system/spritesheet",
		exportName: "SqSpriteSheet",
		category: "atmosphere",
		status: "preferred",
		supersedes: [
			"Ad-hoc CSS spritesheet hacks",
			"Third-party React sprite libs for product UI (e.g. react-sprite-animator)",
		],
		a11yNotes:
			"Honor prefers-reduced-motion (freeze on startFrame). Decorative uses aria-hidden.",
		notes: "Grid/horizontal sheets via public/CDN URLs. fit contain|cover + fitZoom/fitAnchor. Battle/marketing/SaaS characters. Phaser deferred; asset compression deferred.",
	},
	{
		id: "toggle-switch",
		title: "ToggleSwitch",
		importPath: "@repo/ui/components/custom/toggle-switch",
		exportName: "ToggleSwitch",
		category: "chrome",
		status: "preferred",
		supersedes: ["shadcn Switch for that chrome"],
	},
	{
		id: "live-saving-switch",
		title: "LiveSavingSwitch",
		importPath: "@repo/ui/components/live-saving-switch",
		exportName: "LiveSavingSwitch",
		category: "chrome",
		status: "preferred",
		supersedes: ["Ad-hoc save toggles"],
	},
	{
		id: "shortcut-kbd",
		title: "ShortcutKbd",
		importPath: "@repo/ui/components/design-system/shortcut-kbd",
		exportName: "ShortcutKbd",
		category: "chrome",
		status: "preferred",
		supersedes: [
			"Ad-hoc <kbd> rows",
			"Plain mono shortcut text in menus",
		],
		a11yNotes:
			"Decorative by default (aria-hidden) when parent sets aria-keyshortcuts; otherwise role=group.",
		notes:
			"Read-only chord chips for DrilldownMenu / command registries. Segments from formatCommandShortcut (⌘/Ctrl).",
	},
	{
		id: "faded-squares",
		title: "FadedSquares",
		importPath: "@repo/ui/components/custom/faded-squares",
		exportName: "FadedSquares",
		category: "atmosphere",
		status: "preferred",
		supersedes: ["Ad-hoc edge washes"],
		themeModes: ["light", "dark", "system"],
		supportsContrastVariant: true,
		sqColorModePolicy: "theme-pair",
		notes: "contrast = decorative inverse of resolved theme — not a11y high-contrast. See COLOR_MODES.md.",
	},
	{
		id: "magic-cards",
		title: "MagicCards",
		importPath: "@repo/ui/components/react-bits/magic-cards",
		exportName: "MagicCards",
		category: "surface",
		status: "preferred",
		supersedes: ["Nested shadcn Card decks"],
		themeModes: ["light", "dark", "system"],
		sqColorModePolicy: "css-dark-variant",
		notes: "Hub density defaults to ribbonAxis=horizontal (fan at all breakpoints) with wide/medium/compact size tiers from container width. settingsCompact stays 3-up grid. Opt into vertical narrow stack via ribbonAxis=vertical.",
	},
	{
		id: "aura-blob",
		title: "AuraBlob",
		importPath: "@repo/ui/components/react-bits/aura-blob",
		exportName: "AuraBlob",
		category: "atmosphere",
		status: "preferred",
		supersedes: ["Ad-hoc R3F aura shells"],
		themeModes: ["light", "dark", "system"],
		sqColorModePolicy: "static",
		notes: "Decorative nested-shell WebGL aura. Pass color/midColor/deepColor hex for rarity phases. Respects prefers-reduced-motion (pauses). SafeWebGLBoundary — failure renders null.",
	},
	{
		id: "chroma-key-video",
		title: "ChromaKeyVideo",
		importPath: "@repo/ui/components/chroma-key",
		exportName: "ChromaKeyVideo",
		category: "data-display",
		status: "preferred",
		supersedes: [
			"Raw LiveKit video for LemonSlice green streams",
			"Server chroma / HEVC alpha playback as SoT",
		],
		themeModes: ["light", "dark", "system"],
		sqColorModePolicy: "static",
		notes:
			"Client WebGL chroma key for LemonSlice green `#00FF00` streams. compositorBackground NONE (default) = transparent over host UI; STATIC_IMAGE / MOTION_LOOP scaffolded. Renderer attributed from lemonslice-examples. WebGL fail → non-crash unkeyed fallback.",
		a11yNotes: "Decorative keyed plate; host supplies aria labels around the call chrome.",
	},
	{
		id: "model-viewer",
		title: "ModelViewer",
		importPath: "@repo/ui/components/react-bits/model-viewer",
		exportName: "ModelViewer",
		category: "data-display",
		status: "preferred",
		supersedes: ["Ad-hoc R3F model canvases for product inspection"],
		themeModes: ["light", "dark", "system"],
		sqColorModePolicy: "static",
		notes:
			"STATIC GLB/FBX/OBJ inspect only (rotate/zoom/parallax/fade-in). Optional bindRepairUrl prepares Tripo/Mixamo character GLBs at load. Always SafeR3FCanvas. Do NOT compose CharacterBodyCanvas / CharacterSceneCanvas / SceneCharacterPinnedPort or add Mixamo clip banks — Prefer-over split: HUD = CharacterBodyCanvas; battle stage = CharacterSceneCanvas; hub/IC = SceneCharacterPinnedPort; equipment animated Model tab = CharacterBodyInspect.",
		a11yNotes:
			"Honor prefers-reduced-motion (disable autoRotate / hover spin).",
	},
	{
		id: "character-body-canvas",
		title: "CharacterBodyCanvas",
		importPath: "@repo/ui/components/react-bits/character-body-canvas",
		exportName: "CharacterBodyCanvas",
		category: "atmosphere",
		status: "preferred",
		supersedes: ["Ad-hoc R3F character canvases", "HexagonMapCharacterSprite"],
		themeModes: ["light", "dark", "system"],
		sqColorModePolicy: "static",
		notes:
			"HUD Prefer-over Mixamo body (owns SafeR3FCanvas, composes CharacterBodyPresenter): menu portraits, equipment inspect (via CharacterBodyInspect), hex / overlay isolated portraits. Fixed camera presets by default; optional OrbitControls for inspect (defaults off). Do NOT mount on walkable hub/IC (SceneCharacterPinnedPort) or battle (CharacterSceneCanvas). Static product inspect stays ModelViewer; never nest CBC inside ModelViewer.",
		a11yNotes:
			"prefers-reduced-motion holds first frame / skips clip playback and fade-in.",
	},
	{
		id: "character-scene-canvas",
		title: "CharacterSceneCanvas",
		importPath: "@repo/ui/components/react-bits/character-scene-canvas",
		exportName: "CharacterSceneCanvas",
		category: "atmosphere",
		status: "preferred",
		supersedes: [
			"Per-actor CharacterBodyCanvas on battle/hub/IC stages",
			"battle-dual-canvas-gate",
			"SceneCharacterSlot WebGL mounts",
		],
		themeModes: ["light", "dark", "system"],
		sqColorModePolicy: "static",
		notes:
			"Battle stage Prefer-over: one SafeR3FCanvas, N CharacterBodyPresenters, side-biased directional shadows, ShadowOnlyGround, world placement + liveTransforms. Host: BattleCharacterScene. Hub/IC 2.5D uses SceneCharacterPinnedPort; Hub/IC 3D world uses IsometricWorldStage — not this canvas. Not for menu/inspect/hex (use CharacterBodyCanvas).",
		a11yNotes:
			"prefers-reduced-motion honored via presenter clip path; canvas remains pointer-events-none under DOM chrome.",
	},
	{
		id: "isometric-world-stage",
		title: "IsometricWorldStage",
		importPath: "@repo/ui/components/isometric-world/isometric-world-stage",
		exportName: "IsometricWorldStage",
		category: "atmosphere",
		status: "preferred",
		supersedes: [],
		themeModes: ["light", "dark", "system"],
		sqColorModePolicy: "static",
		notes:
			"Prefer-over for Scene Studio + Hub/IC **3D world** path (iso program P0–P8 SHIPPED): one SafeR3FCanvas, fixed top-down iso camera, SceneTerrainMesh + ScenePropMesh + Mixamo characterActors (liveSeatsRef) + shadows. Gate: NEXT_PUBLIC_SCENE_3D_BUILD_ENABLED + TERRAIN_3D + worldFloorKind ≠ NONE. Dual path — 2.5D pins remain for PLATE_2D/PROPS_3D. Battle stays CharacterSceneCanvas. See docs/architecture/isometric-world-stage.md.",
		a11yNotes:
			"prefers-reduced-motion via presenters; honor reduced-motion in Build World GridReveal (skip anim, keep approve).",
	},
	{
		id: "scene-character-pinned-canvas",
		title: "SceneCharacterPinnedPort / View",
		importPath: "@repo/ui/components/react-bits/scene-character-pinned-canvas",
		exportName: "SceneCharacterPinnedPort",
		category: "atmosphere",
		status: "preferred",
		supersedes: [
			"Hub/IC CharacterSceneCanvas shared stage",
			"SceneCharacterSlot WebGL mounts",
			"Per-actor CharacterBodyCanvas on hub/IC",
		],
		themeModes: ["light", "dark", "system"],
		sqColorModePolicy: "static",
		notes:
			"Hub/IC **2.5D** Prefer-over (still supported): one SafeR3FCanvas + View.Port and N SceneCharacterPinnedView on CSS sprite pins. Gameplay scene-norm; feet = overlay pins. Host: SceneCharacterScene + ScenePinnedDoll. **3D world path** uses IsometricWorldStage instead (dual path). Battle stays CharacterSceneCanvas.",
		a11yNotes:
			"prefers-reduced-motion via presenter; pins are pointer-events-none under DOM chrome.",
	},
	{
		id: "character-body-inspect",
		title: "CharacterBodyInspect",
		importPath: "@repo/ui/components/react-bits/character-body-inspect",
		exportName: "CharacterBodyInspect",
		category: "data-display",
		status: "preferred",
		supersedes: [
			"MixamoDualStanceClipHarness",
			"ModelViewer animationInspect / CBC compose",
		],
		themeModes: ["light", "dark", "system"],
		sqColorModePolicy: "static",
		notes:
			"Equipment animated Model-tab Prefer-over: CharacterBodyCanvas + orbit/autoRotate/zoom + theme-safe clip toolbar (standing bank). Own canvas — never ModelViewer composing CBC. Host supplies i18n aria labels.",
		a11yNotes:
			"Honor prefers-reduced-motion (disable autoRotate; hold idle). Clip toolbar buttons need aria-pressed + host aria-label.",
	},
	{
		id: "simple-swirl",
		title: "SimpleSwirl",
		importPath: "@repo/ui/components/react-bits/simple-swirl",
		exportName: "SimpleSwirl",
		category: "atmosphere",
		status: "allowed",
		supersedes: [],
		themeModes: ["light", "dark", "system"],
		sqColorModePolicy: "static",
		notes: "Concentric swirl WebGL (R3F). Scene portals Prefer-over `Portal` instead. Respects prefers-reduced-motion. SafeWebGLBoundary.",
	},
	{
		id: "portal",
		title: "Portal",
		importPath: "@repo/ui/components/react-bits/portal",
		exportName: "Portal",
		category: "atmosphere",
		status: "preferred",
		supersedes: ["SimpleSwirl for scene portals", "Ad-hoc portal shaders"],
		themeModes: ["light", "dark", "system"],
		sqColorModePolicy: "static",
		notes: "Particle tunnel portal ball (Three.js shader). ScenePortalMarkers uses defaults + scale 0.55. Respects prefers-reduced-motion. SafeWebGLBoundary.",
	},
	{
		id: "magic-bento",
		title: "MagicBento",
		importPath: "@repo/ui/components/react-bits/magic-bento",
		exportName: "MagicBento",
		category: "surface",
		status: "preferred",
		supersedes: ["DIY bento grids"],
		themeModes: ["light", "dark", "system"],
		sqColorModePolicy: "css-dark-variant",
	},
	{
		id: "hextile-background",
		title: "HexTileBackground",
		importPath: "@repo/ui/components/custom/hextile-background",
		exportName: "HexTileBackground",
		category: "atmosphere",
		status: "preferred",
		supersedes: ["Random hex fills as main container bg"],
		themeModes: ["light", "dark", "system"],
		sqColorModePolicy: "css-dark-variant",
		a11yNotes: "Decorative — aria-hidden where appropriate.",
	},
	{
		id: "dot-field",
		title: "DotField",
		importPath: "@repo/ui/components/react-bits/dot-field",
		exportName: "DotField",
		category: "atmosphere",
		status: "preferred",
		supersedes: ["BlinkingSquares as page wash"],
		a11yNotes: "Canvas/graph fill — not main container bg.",
	},
	{
		id: "blinking-squares",
		title: "BlinkingSquares",
		importPath: "@repo/ui/components/react-bits/blinking-squares",
		exportName: "BlinkingSquares",
		category: "atmosphere",
		status: "preferred",
		supersedes: [],
		notes: "R3F accent only — not main container background.",
	},
	{
		id: "sparkle-stars",
		title: "SparkleStars",
		importPath: "@repo/ui/components/custom/sparkle-stars",
		exportName: "SparkleStars",
		category: "atmosphere",
		status: "preferred",
		supersedes: [
			"Inline shine-stars.png bands",
			"Ad-hoc sparkle Image stacks",
		],
		notes: "Staggered shine-star field. Layouts: levelUpBand (shine-stars.png bboxes) | halo (ellipse). Motion: ease-in-out expand/contract via transform-origin. Configure cycleSec / scaleMin–Max / opacityMin–Max / stars[]. Use behind level-up heroes, unclaimed loot chests, badges.",
		a11yNotes: "Decorative — aria-hidden. Honors prefers-reduced-motion.",
	},
	{
		id: "glass-panel-ui",
		title: "GlassPanel (@repo/ui)",
		importPath: "@repo/ui/components/custom/glass-panel",
		exportName: "GlassPanel",
		category: "surface",
		status: "preferred",
		supersedes: ["Card + blur for Operate glass chrome"],
		themeModes: ["light", "dark", "system"],
		supportsLocalTone: true,
		sqColorModePolicy: "css-dark-variant",
	},
	{
		id: "floating-inset-panel",
		title: "FloatingInsetPanel",
		importPath: "@repo/ui/components/design-system/floating-inset-panel",
		exportName: "FloatingInsetPanel",
		category: "surface",
		status: "preferred",
		supersedes: [
			"Full-height second sidebars over a stage",
			"Ad-hoc absolute glass cards inside maps / scenes",
		],
		themeModes: ["light", "dark", "system"],
		sqColorModePolicy: "css-dark-variant",
		a11yNotes:
			"Labelled <section>; labelled close button; the host moves focus in on open and back to its toggle on close. Narrow stages dock it as a bottom sheet.",
		notes: "Glass panel that opens and closes inside a stage, inset from its top / right edges (marketplace-ea D22; learn module Structure, Scene Studio panels). Host is a `@container/stage` element. Inset + z tokens: FLOATING_INSET_PANEL_INSET_CLASS / FLOATING_INSET_PANEL_Z_CLASS.",
	},
	{
		id: "glass-panel-shared",
		title: "GlassPanel (@shared marketing)",
		importPath: "@shared/components/GlassPanel",
		exportName: "GlassPanel",
		category: "surface",
		status: "allowed",
		supersedes: [],
		notes: "Marketing/checkout panel composition; Operate chrome prefers @repo/ui glass.",
	},

	// —— Loaders ——
	{
		id: "dotm-hex1-bloom-center",
		title: "DotmHex1BloomCenter",
		importPath: "@repo/ui/components/dotmatrix",
		exportName: "DotmHex1BloomCenter",
		category: "loader",
		status: "preferred",
		supersedes: [
			"Ad-hoc panel spinners",
			"Raw dotmatrix-core imports in apps",
		],
		notes: "Section / panel loading SoT.",
	},
	{
		id: "spinner",
		title: "Spinner",
		importPath: "@repo/ui/components/spinner",
		exportName: "Spinner",
		category: "loader",
		status: "preferred",
		supersedes: ["Dotmatrix for one-line inline processing"],
		notes: "Inline processing + text — not section loaders.",
	},

	// —— Overlays / sheets / feedback ——
	{
		id: "gate-overlay",
		title: "Gate* overlays",
		importPath: "@saas/shared/components/gates/GateOverlay",
		exportName: "GateOverlay",
		category: "overlay",
		status: "preferred",
		supersedes: ["Ad-hoc frosted empty/eligibility overlays"],
		notes: "Stay in place (SaaS-coupled). Family under @saas/shared/components/gates/*.",
	},
	{
		id: "sheet",
		title: "Sheet",
		importPath: "@repo/ui/components/sheet",
		exportName: "Sheet",
		category: "overlay",
		status: "preferred",
		supersedes: ["Wrong z-index drawers covering header"],
		notes: "Pair with saas-sheet-chrome helpers where applicable. Still Radix Dialog under the hood — not replaced by headless Dialog.",
	},
	{
		id: "headless-dialog",
		title: "Dialog (Headless UI + Motion)",
		importPath: "@repo/ui/components/dialog",
		exportName: "Dialog",
		category: "overlay",
		status: "preferred",
		supersedes: [
			"@radix-ui/react-dialog for product modals",
			"Raw Headless Dialog without product facade",
			"@repo/ui/components/animate-ui/.../headless/dialog (removed)",
		],
		notes: "Hybrid facade: Radix-shaped API (`open`/`onOpenChange`, `DialogContent`, `DialogTrigger`) on Headless UI + Motion under `components/headless-ui`. Default close = CircleButton `xs` (`showCloseButton`). **Footer actions must use `DialogFooterActions` + PrimaryCta/SecondaryCta/DestructiveCta** (`ctaWidth` auto: grow for two, full for one) — never raw shadcn `Button` as dialog CTAs. Sheet / AlertDialog stay Radix. Escape duplicate close: `showCloseButton={false}`. Non-modal marketing Portal overlays: use `@radix-ui/react-dialog` Root+Portal directly.",
		a11yNotes:
			"Headless UI Dialog focus trap + Title/Description. Overlay/panel z-[200].",
	},
	{
		id: "app-context-menu",
		title: "AppContextMenu",
		importPath: "@saas/shared/components/context-menu/AppContextMenu",
		exportName: "AppContextMenu",
		category: "overlay",
		status: "preferred",
		supersedes: [
			"Raw @repo/ui/components/shadcn/context-menu without long-press",
			"Ad-hoc right-click-only ContextMenu hosts",
		],
		notes:
			"Prefer-over context menu: right-click + long-press (~500ms) via usePressHoldGesture. Re-exports ContextMenuItem/Sub* for menu bodies. Inventory + SceneEditor Insert/marker menus.",
		a11yNotes:
			"Keyboard-accessible via Radix ContextMenu; long-press for touch. Prefer ContextMenuItem labels.",
	},
	{
		id: "content-tooltip",
		title: "ContentTooltip",
		importPath: "@repo/ui/components/design-system/tooltip/content-tooltip",
		exportName: "ContentTooltip",
		category: "overlay",
		status: "preferred",
		supersedes: [
			"LazyMountTooltip",
			"AnimatedTooltip / shadcn-space tooltip-03",
			"TapOrHoverTooltip",
			"shadcn Tooltip for product tips",
			"Ad-hoc blue demo tooltips",
		],
		themeModes: ["light", "dark", "system"],
		sqColorModePolicy: "css-dark-variant",
		notes: "Prefer-over content tip shell. Props: colorScheme system|invert|light|dark (default system = light twin + ability #0f1320 dark SoT; forced dark = ABILITY_DARK), surface default|inverse|glass, size sm|md|lg, transition fadeZoom|springTilt (default fadeZoom), delayDuration default 0, showArrow default !followCursor, autoPlace (default true), exclusive tip singleton (independent of DropdownMenu exclusive), followCursor, interactive safe-polygon, lazyMount. Bags: SHORT / CHROME (hover-only for actionable header controls) / HUD (system) / ABILITY_DARK / LOOT. Portal z = CONTENT_TOOLTIP_Z_INDEX (320). Hard-cut: no LazyMount/AnimatedTooltip/TapOrHover/`@repo/ui/components/tooltip` product tips.",
		a11yNotes:
			"role=tooltip; hover/focus or tap per openOn; exclusive open (one portal); reduced-motion disables springTilt.",
	},
	{
		id: "dialog-footer-actions",
		title: "DialogFooterActions",
		importPath: "@repo/ui/components/dialog",
		exportName: "DialogFooterActions",
		category: "overlay",
		status: "preferred",
		supersedes: [
			"DialogFooter + raw Button",
			"Ad-hoc justify-end Button pairs in product dialogs",
		],
		notes: "Wrap PrimaryCta / SecondaryCta / DestructiveCta. Two children → half width (`ctaWidth=grow`); one child → full width (`ctaWidth=full`).",
	},
	{
		id: "headless-dialog-parts",
		title: "Headless dialog parts",
		importPath: "@repo/ui/components/headless-ui",
		exportName: "HeadlessDialog",
		category: "overlay",
		status: "allowed",
		notes: "Low-level Headless+Motion parts. Prefer `@repo/ui/components/dialog` for product modals.",
	},
	{
		id: "snap-drag-sheet",
		title: "SnapDragSheet",
		importPath: "@repo/ui/components/custom/snap-drag-sheet",
		exportName: "SnapDragSheet",
		category: "overlay",
		status: "preferred",
		supersedes: ["Wrong snap drawer implementations"],
	},
	{
		id: "side-drawer",
		title: "SideDrawer",
		importPath: "@shared/components/top-drawer/SideDrawer",
		exportName: "SideDrawer",
		category: "overlay",
		status: "preferred",
		supersedes: [
			"Ad-hoc SnapDragSheet chrome for in-container drawers",
			"One-off top/bottom sheet shells that reimplement dismiss/expand",
		],
		notes: "Directional in-container drawer (top|bottom|left|right). TopDrawer/BottomDrawer are thin wrappers. Prefer over inventing new snap shells; migrate learn/entity/pricing custom drawers over time.",
	},
	{
		id: "top-drawer",
		title: "TopDrawer",
		importPath: "@shared/components/top-drawer/TopDrawer",
		exportName: "TopDrawer",
		category: "overlay",
		status: "preferred",
		supersedes: [],
		notes: "Convenience wrapper: SideDrawer side=top. Stay in place — register only; heavy shared chrome.",
	},
	{
		id: "bottom-drawer",
		title: "BottomDrawer",
		importPath: "@shared/components/top-drawer/BottomDrawer",
		exportName: "BottomDrawer",
		category: "overlay",
		status: "preferred",
		supersedes: [],
		notes: "Convenience wrapper: SideDrawer side=bottom. Battle narrow workspace menu; same dismiss/expand contract as TopDrawer.",
	},
	{
		id: "sonner",
		title: "Sonner toaster",
		importPath: "@repo/ui/components/sonner",
		exportName: "toast",
		category: "feedback",
		status: "preferred",
		supersedes: ["Direct sonner package imports in apps"],
		notes: "deny:raw-sonner enforces SoT file only.",
	},
	{
		id: "sq-stat-badge",
		title: "SqStatBadge",
		importPath: "@repo/ui/components/custom/sq-stat-badge",
		exportName: "SqStatBadge",
		category: "data-display",
		status: "preferred",
		supersedes: ["One-off status chips"],
		themeModes: ["light", "dark", "system"],
		sqColorModePolicy: "css-dark-variant",
		notes: "Soft variants use --sq-soft-* under .dark (COLOR_MODES.md).",
	},
	{
		id: "status-callout",
		title: "StatusCallout",
		importPath: "@repo/ui/components/status-callout",
		exportName: "StatusCallout",
		category: "feedback",
		status: "preferred",
		supersedes: ["FormMessage dual chrome for page errors"],
	},
	{
		id: "daisy-countdown",
		title: "DaisyCountdown",
		importPath: "@repo/ui/components/daisy-ui/countdown",
		exportName: "DaisyCountdown",
		category: "data-display",
		status: "preferred",
		supersedes: ["Hand-rolled countdown digits"],
	},
	{
		id: "octagon-button",
		title: "OctagonButton / OctagonBadge",
		importPath: "@repo/ui/components/octagon-button/octagon-button",
		exportName: "OctagonButton",
		category: "action",
		status: "preferred",
		supersedes: ["Rounded Badge for equip chrome"],
		supportsLocalTone: true,
	},
	{
		id: "skill-badges",
		title: "SkillBadges",
		importPath: "@repo/ui/components/octagon-badge/skill-badges",
		exportName: "SkillBadges",
		category: "data-display",
		status: "preferred",
		supersedes: ["Ad-hoc skill icon rows"],
		notes: '`layout="carousel"` (default) or `layout="group"` (ContentTooltip morph stack with shared groupId). Entity structure headers use group.',
	},
	{
		id: "animate-avatar-group",
		title: "AvatarGroup (Animate UI)",
		importPath:
			"@repo/ui/components/animate-ui/components/animate/avatar-group",
		exportName: "AvatarGroup",
		category: "data-display",
		status: "preferred",
		supersedes: ["Hand-rolled overlapping avatar stacks"],
		notes: 'Prefer for compact overlapping people/skill stacks. SkillBadges `layout="group"` composes this.',
	},

	// —— Brand / palette ——
	{
		id: "logo-saas",
		title: "Logo (SaaS app)",
		importPath: "@saas/shared/components/Logo",
		exportName: "Logo",
		category: "brand",
		status: "preferred",
		supersedes: ["Wrong logo API in app chrome"],
		notes: "next/image app header. See logo matrix in COMPONENT_CATALOG.",
	},
	{
		id: "logo-ui",
		title: "Logo (@repo/ui)",
		importPath: "@repo/ui/components/logo",
		exportName: "Logo",
		category: "brand",
		status: "preferred",
		supersedes: [],
		notes: "SVG shells / mail-aligned.",
	},
	{
		id: "logo-email",
		title: "EmailLogo",
		importPath: "@repo/mail/components/EmailLogo",
		exportName: "EmailLogo",
		category: "brand",
		status: "preferred",
		supersedes: [],
		notes: "Mail package only.",
	},
	{
		id: "sq-colors",
		title: "sq-colors palette",
		importPath: "@repo/ui/lib/sq-colors",
		exportName: "SQ_COLORS",
		category: "brand",
		status: "preferred",
		supersedes: ["Hardcoded brand hex outside tokens"],
		sqColorModePolicy: "theme-pair",
	},

	// —— Collections / search / tiles / DnD ——
	{
		id: "entity-viewer",
		title: "EntityViewer stack",
		importPath: "@saas/shared/components/entity-viewer",
		exportName: "EntityViewer",
		category: "collection",
		status: "preferred",
		supersedes: [
			"Custom list grids",
			"Ad-hoc sort/filter/pager",
			"Sticky category headers inside tile scrollport",
		],
		relatedLearningsAnchor:
			"Entity viewer category dock — scroll-spy slack; ScrollFadeEdges; empty host",
		notes: "Use CreateEntityCollection / MixedEntityCollection hosts. CategoryDock spy outside scrollport.",
	},
	{
		id: "entity-list-scaffold",
		title: "EntityListScaffold",
		importPath: "@saas/shared/components/entity-viewer",
		exportName: "EntityListScaffold",
		category: "collection",
		status: "preferred",
		supersedes: ["Full EntityViewer when sidebar weight is wrong"],
		relatedLearningsAnchor:
			"When to use EntityListScaffold vs CreateEntityCollection",
	},
	{
		id: "entity-search-field",
		title: "EntitySearchField",
		importPath: "@saas/shared/components/entity-search",
		exportName: "EntitySearchField",
		category: "collection",
		status: "preferred",
		supersedes: [
			"EntityViewerSearchField",
			"EntitySearchExpandedField",
			"Forked search fields",
		],
		relatedLearningsAnchor: "EntitySearchField only",
		notes: "deny:entity-search-forks. Only search SoT.",
	},
	{
		id: "entity-tile",
		title: "EntityTile",
		importPath: "@saas/shared/components/entity-tiles/entity-tile",
		exportName: "EntityTile",
		category: "collection",
		status: "preferred",
		supersedes: ["One-off hub cards/rows"],
		notes: "Shared chrome; entity-specific face/content via kind/variant.",
	},
	{
		id: "entity-structure-tile",
		title: "EntityStructureTile",
		importPath:
			"@saas/ai/components/builder-chat/structure-editor/entity-structure-tile",
		exportName: "EntityStructureTile",
		category: "collection",
		status: "preferred",
		supersedes: [
			"One-off accordion rows",
			"Bespoke badge/ellipsis structure chrome",
		],
		notes: "Shared accordion/nesting/badges/menu/DnD; only face/data/menu actions vary.",
	},
	{
		id: "structure-dnd-sortable",
		title: "SortableItem / structure DnD",
		importPath: "@saas/ai/components/builder-chat/structure-editor/dnd",
		exportName: "SortableItem",
		category: "dnd",
		status: "preferred",
		supersedes: ["Raw @dnd-kit per structure surface"],
		notes: "Also useDraftDndSensors → useSqDndSensors SoT (mouse distance + touch press-hold), DragHandle — structure-editor DnD.",
	},
	{
		id: "entity-structure-expand-set",
		title: "useEntityStructureExpandSet",
		importPath:
			"@saas/ai/components/builder-chat/structure-editor/use-entity-structure-expand-set",
		exportName: "useEntityStructureExpandSet",
		category: "disclosure",
		status: "preferred",
		supersedes: ["Ad-hoc expand Sets per editor"],
	},
	{
		id: "entity-viewer-empty-host",
		title: "ENTITY_VIEWER_EMPTY_STATE_HOST_CLASS",
		importPath: "@saas/shared/components/entity-viewer",
		exportName: "ENTITY_VIEWER_EMPTY_STATE_HOST_CLASS",
		category: "collection",
		status: "preferred",
		supersedes: ["min-h-full alone under scroll parents"],
		relatedLearningsAnchor: "Entity viewer empty",
	},

	// —— Quiz player ——
	{
		id: "quiz-player-shell",
		title: "QuizPlayerShell",
		importPath: "@repo/ui/components/quiz/QuizPlayerShell",
		exportName: "QuizPlayerShell",
		category: "chrome",
		status: "preferred",
		supersedes: ["BattleBoostBlock local banner/timer/nav"],
		themeModes: ["light", "dark", "system"],
		a11yNotes:
			"aria-live for correct/incorrect; announceHoldMs before continuation ring",
		notes: "Immersive @container/quiz shell — banner, CircleButton nav, continuation timer.",
	},
	{
		id: "quiz-answer-tile",
		title: "QuizAnswerTile",
		importPath: "@repo/ui/components/quiz/quiz-answer-tile",
		exportName: "QuizAnswerTile",
		category: "action",
		status: "preferred",
		supersedes: ["shadcn Button outline answer tiles"],
		notes: "Equalized MC Ripple tiles; Grainient-aligned reveal fills @95% + deep borders; clipped Rising (green) / reverse Rising (red) on chosen tiles; emboldened correct text; tick/cross on reveal; no hover when answered. Used by MCS/MCM renderers.",
	},
	{
		id: "quiz-feedback-layer",
		title: "QuizAnswerFeedbackLayer",
		importPath: "@repo/ui/components/quiz/quiz-answer-feedback-layer",
		exportName: "QuizAnswerFeedbackLayer",
		category: "feedback",
		status: "preferred",
		supersedes: ["Text-only Correct/Incorrect"],
		notes: "Window-level Sq green confetti from correct answer tile originRect; tile owns tick/cross + Rising + Grainient fills; preloadQuizFeedbackVfx; zIndex aligns with Z_INDEX.saasQuizAnswerFeedback; reduced-motion skips confetti.",
	},
	{
		id: "quiz-play-stage",
		title: "QuizPlayStage",
		importPath: "@repo/ui/components/quiz/quiz-play-stage",
		exportName: "QuizPlayStage",
		category: "chrome",
		status: "preferred",
		supersedes: [
			"Centered non-scrolling quiz body that clips under banner",
		],
		notes: "Scroll host for question body inside QuizPlayerShell.",
	},
	{
		id: "quiz-token-chip",
		title: "QuizTokenChip",
		importPath: "@repo/ui/components/quiz/quiz-token-chip",
		exportName: "QuizTokenChip",
		category: "action",
		status: "preferred",
		supersedes: ["Ad-hoc Ripple/Button chips per format"],
		notes: "Bank/reorder/pair chip; used state stays muted in tray.",
	},
	{
		id: "quiz-blank-slot",
		title: "QuizBlankSlot",
		importPath: "@repo/ui/components/quiz/quiz-blank-slot",
		exportName: "QuizBlankSlot",
		category: "action",
		status: "preferred",
		supersedes: ["Raw {{blank}} / unstyled inputs"],
		notes: "FIB empty/active/filled slot; filled is clearable.",
	},
	{
		id: "quiz-word-bank-tray",
		title: "QuizWordBankTray",
		importPath: "@repo/ui/components/quiz/quiz-word-bank-tray",
		exportName: "QuizWordBankTray",
		category: "collection",
		status: "preferred",
		supersedes: ["Unlabeled flex-wrap chip lists"],
		notes: "Labeled wrap tray; used chips remain laid out muted.",
	},
	{
		id: "quiz-ordinal-badge",
		title: "QuizOrdinalBadge",
		importPath: "@repo/ui/components/quiz/quiz-ordinal-badge",
		exportName: "QuizOrdinalBadge",
		category: "chrome",
		status: "preferred",
		supersedes: ["Plain text rank indices"],
		notes: "Rank ladder ordinal chrome.",
	},
	{
		id: "quiz-truncation-scroll",
		title: "QuizTruncationScroll",
		importPath: "@repo/ui/components/quiz/quiz-truncation-scroll",
		exportName: "QuizTruncationScroll",
		category: "chrome",
		status: "preferred",
		supersedes: ["Per-format marquee / ellipsis-only truncation"],
		notes: "Horizontal or vertical auto-scroll when overflowing; speed/pause/reset|reverse; reduced-motion idle.",
	},
	{
		id: "quiz-pair-chords",
		title: "QuizPairChords",
		importPath: "@repo/ui/components/quiz/quiz-pair-chords",
		exportName: "QuizPairChords",
		category: "chrome",
		status: "preferred",
		supersedes: ["Pair columns without visible connectors"],
		notes: "SVG bezier chords between paired left/right; ResizeObserver; pointer-events-none; --sq-quiz-chord.",
	},

	// —— Scene Studio (PS hard-cut shell; P1+ bbox/approve/meshes) ——
	{
		id: "scene-studio-shell",
		title: "SceneStudioShell",
		importPath: "@repo/ui/components/design-system/scene-studio",
		exportName: "SceneStudioShell",
		category: "chrome",
		status: "preferred",
		supersedes: ["SceneEditor Dialog", "ic-scene-editor-dialog"],
		notes:
			"Full-bleed Scene Studio page shell. Hard-cut: no Dialog SceneEditor dual path.",
	},
	{
		id: "scene-studio-canvas",
		title: "SceneStudioCanvas",
		importPath: "@repo/ui/components/design-system/scene-studio",
		exportName: "SceneStudioCanvas",
		category: "atmosphere",
		status: "preferred",
		supersedes: ["SceneEditor dialog canvas"],
		notes: "PanZoomSurface SoT + Morph/R3F content root; zero padding.",
	},
	{
		id: "scene-studio-layers-rail",
		title: "SceneStudioLayersRail",
		importPath:
			"@saas/create/modules/ic-scenes/scene-studio/SceneStudioLayersRail",
		exportName: "SceneStudioLayersRail",
		category: "chrome",
		status: "preferred",
		supersedes: ["SceneEditorLayersPanel"],
	},
	{
		id: "scene-studio-assets-panel",
		title: "SceneStudioAssetsPanel",
		importPath:
			"@saas/create/modules/ic-scenes/scene-studio/SceneStudioAssetsPanel",
		exportName: "SceneStudioAssetsPanel",
		category: "collection",
		status: "preferred",
		supersedes: ["SceneEditorLibraryPanel"],
		notes: "Right-rail ASSETS tab content (LME INFO/CHAT pattern).",
	},
	{
		id: "scene-studio-tool-bar",
		title: "SceneStudioToolBar",
		importPath: "@repo/ui/components/design-system/scene-studio",
		exportName: "SceneStudioToolBar",
		category: "chrome",
		status: "preferred",
		notes: "Always-visible context-aware tool strip.",
	},
	{
		id: "scene-studio-stage-cta-bar",
		title: "SceneStudioStageCtaBar",
		importPath: "@repo/ui/components/design-system/scene-studio",
		exportName: "SceneStudioStageCtaBar",
		category: "action",
		status: "preferred",
		notes: "Bottom primary/secondary CTAs only when a build stage needs action.",
	},
	{
		id: "scene-studio-lightboard",
		title: "SceneStudioLightboard",
		importPath: "@repo/ui/components/design-system/scene-studio",
		exportName: "SceneStudioLightboard",
		category: "chrome",
		status: "preferred",
		notes: "MV / crop / estimate strip — not a Dialog workflow shell.",
	},
	{
		id: "scene-studio-bbox-overlay",
		title: "SceneStudioBBoxOverlay",
		importPath: "@repo/ui/components/design-system/scene-studio",
		exportName: "SceneStudioBBoxOverlay",
		category: "chrome",
		status: "preferred",
		notes: "Native classify approve overlays on pan-zoom content root.",
	},
	{
		id: "scene-studio-extract-layer-overlay",
		title: "SceneStudioExtractLayerOverlay",
		importPath: "@repo/ui/components/design-system/scene-studio",
		exportName: "SceneStudioExtractLayerOverlay",
		category: "chrome",
		status: "preferred",
		supersedes: [
			"ad-hoc crop thumb strip as primary Precise extract SoT",
		],
		notes:
			"Precise PREVIEW_READY only: tight crop + placementNorm. Never use for Layerize — use SceneStudioLayerizeOverlay.",
	},
	{
		id: "scene-studio-layerize-overlay",
		title: "SceneStudioLayerizeOverlay",
		importPath: "@repo/ui/components/design-system/scene-studio",
		exportName: "SceneStudioLayerizeOverlay",
		category: "chrome",
		status: "preferred",
		supersedes: [
			"SceneStudioExtractLayerOverlay for Layerize review",
			"bboxJson AABB framing for Layerize cutouts",
		],
		notes:
			"Layerize hard cut: full-frame inset-0 RGBA stack; pixel outline when selected+outlinePathJson; else full-stage rim; orderIndex paint; no AABB/placementNorm framing.",
	},
	{
		id: "scene-studio-extract2d-interact-stage",
		title: "SceneStudioExtract2dInteractStage",
		importPath: "@repo/ui/components/design-system/scene-studio",
		exportName: "SceneStudioExtract2dInteractStage",
		category: "chrome",
		status: "preferred",
		supersedes: [
			"ENV chip markers for extract2d placements",
			"collision AABB as primary ENV transform chrome",
		],
		notes:
			"Post-Apply extract2d: alpha hit-test select, pixel-bounds transform chrome, move + aspect-locked resize (Alt from center, Shift free). Pair with SceneStudioPlacementTransformChrome.",
	},
	{
		id: "scene-studio-placement-transform-chrome",
		title: "SceneStudioPlacementTransformChrome",
		importPath: "@repo/ui/components/design-system/scene-studio",
		exportName: "SceneStudioPlacementTransformChrome",
		category: "chrome",
		status: "preferred",
		supersedes: ["ad-hoc placement move/resize handles"],
		notes:
			"Figma-like selection frame + 8 handles; data-pan-zoom-ignore. Used by extract2d interact stage.",
	},
	{
		id: "scene-studio-layer-matte-brush",
		title: "SceneStudioLayerMatteBrush",
		importPath: "@repo/ui/components/design-system/scene-studio",
		exportName: "SceneStudioLayerMatteBrush",
		category: "chrome",
		status: "preferred",
		supersedes: [
			"ad-hoc canvas matte editors for Layerize HITL",
		],
		notes:
			"P6 Layerize HITL: erase/restore alpha, soft edge, session undo, Save → updateLayerCandidateMatte. prefers-reduced-motion: no required motion.",
	},
	{
		id: "compact-toggle-include-actions",
		title: "CompactToggleIncludeActions",
		importPath: "@repo/ui/components/custom/editable-field",
		exportName: "CompactToggleIncludeActions",
		category: "action",
		status: "preferred",
		supersedes: [
			"ad-hoc tick/cross include toggles for Scene Studio extract",
		],
		notes:
			"Extract include/exclude chrome (≥44×44 hit). Use with SceneStudioBboxObjectChrome.",
	},
	{
		id: "scene-studio-bbox-object-chrome",
		title: "SceneStudioBboxObjectChrome",
		importPath: "@repo/ui/components/design-system/scene-studio",
		exportName: "SceneStudioBboxObjectChrome",
		category: "chrome",
		status: "preferred",
		supersedes: [
			"ad-hoc bbox include/reorder chrome for Scene Studio extract",
		],
		notes:
			"Per-bbox tick/cross + peel chevrons/index, or ellipsisOnly overflow menu.",
	},
	{
		id: "scene-studio-approve-list",
		title: "SceneStudioApproveList",
		importPath: "@repo/ui/components/design-system/scene-studio",
		exportName: "SceneStudioApproveList",
		category: "chrome",
		status: "preferred",
		notes: "Layers-rail a11y/list sync for selection schema.",
	},
	{
		id: "scene-prop-mesh",
		title: "ScenePropMesh",
		importPath: "@repo/ui/components/design-system/scene-studio",
		exportName: "ScenePropMesh",
		category: "atmosphere",
		status: "preferred",
		notes:
			"Prop GLB on CharacterSceneCanvas / hub-IC pins / IsometricWorldStage. Optional clipUrl (useAnimations) + fxPreset amorphous_particles. Missing clip → static. Material tint overrides (iso Tier C) apply without remesh — SkinTokens is rig-only, not retexture.",
	},
	{
		id: "scene-prop-particle-fx",
		title: "ScenePropParticleFx",
		importPath: "@repo/ui/components/design-system/scene-studio",
		exportName: "ScenePropParticleFx",
		category: "atmosphere",
		status: "preferred",
		notes:
			"Amorphous prop FX (preset amorphous_particles). No Baseten. Hidden under reduced-motion.",
	},
	{
		id: "scene-terrain-mesh",
		title: "SceneTerrainMesh",
		importPath: "@repo/ui/components/design-system/scene-studio",
		exportName: "SceneTerrainMesh",
		category: "atmosphere",
		status: "preferred",
		notes:
			"Heightfield terrain.glb when scenePresentationMode=TERRAIN_3D / world floor. Ground mesh for IsometricWorldStage (iso program) and legacy TERRAIN_3D pin stamp until world stage cutover.",
	},

	// —— Legacy avoid ——
	{
		id: "theme-switcher-legacy",
		title: "ThemeSwitcher (tailwind)",
		importPath: "@repo/ui/components/tailwind/theme-switcher",
		exportName: "ThemeSwitcher",
		category: "chrome",
		status: "avoid",
		supersedes: [],
		notes: "Prefer ColorModeToggle (@shared). Do not use for new UI.",
	},
];

export function getPreferredDesignSystemComponents(): DesignSystemComponentEntry[] {
	return DESIGN_SYSTEM_COMPONENTS.filter(
		(entry) => entry.status === "preferred",
	);
}

export function getDesignSystemComponentById(
	id: string,
): DesignSystemComponentEntry | undefined {
	return DESIGN_SYSTEM_COMPONENTS.find((entry) => entry.id === id);
}
